/**
 * 재고 현황 — 탭 셋: 구매 건(기본 — 구매 줄 하나 = 한 줄, 2026-10-19) · 실물 · 이력. 같은 화면이 메뉴 변동표(기간 요약 → 줄을 누르면 일별)도 그린다 — 탭 줄 없이(2026-10-26).
 * 구매 건은 백엔드 /api/admin/inventory/stock-lines — 줄을 누르면 재고 상세 새 탭(드래그 선택이면 안 연다, 2026-10-26). 참고용, 작업은 실물 탭.
 * 실물은 백엔드 /api/admin/inventory/units — 실물 한 장 = 한 줄(현민 2026-10-08 "시리얼 기준"). 서버 속 부품은 줄이 아니라 서버 줄의 "안에 n장".
 * 상태 · 검색 · 나간 장 보이기는 서버가 거른다. 한 페이지 500(제품 목록과 같다). 2026-10-14 실데이터로(시안 2026-10-06).
 * 서랍(제품 상세) · 체크 · 액션 버튼은 시안 그대로 — 동작은 다음 공사. 변동표는 가짜 데이터(inventory_event 라우트가 아직 없다).
 */

import { SolaView, htmlSolaWithInstance, navigate } from '@hm-spt/sola';
import * as api from '../api/client';
import { costTextOf } from '../lib/money';
import type { EStockLineSource, EStockStatus, StockFlowDayList, StockFlowDayQuery, StockFlowDayView, StockFlowListQuery, StockFlowView, StockFlowViewList, StockLineListQuery, StockLineView, StockLineViewList, UnitEventLineView, UnitEventLineViewList, UnitEventListQuery, UnitListQuery, UnitView, UnitViewList } from '../api/client';
import { DetailDrawer } from '../components/DetailDrawer';
import { renderPagerLine } from '../components/pager-line';
import { EVENT_WORD, ORIGIN_WORD, REASON_WORD, STATUS_WORD, eventWordOf, pnTextOf, renderUnitTable, valueWordOf } from '../components/unit-table';
import { isRetireReady, renderRetireDialog, retireBlockOf, retireInputOf } from '../components/retire-dialog';
import type { RetireDraft } from '../components/retire-dialog';
import { assert } from '../lib/assert';
import { hasTextSelection } from '../lib/selection';
import { escapeHtml, fmtDateTime } from '../lib/cells';
import { ROW_NO_HEAD, rowNoCell } from '../components/row-no';
import { SPEC_CATEGORIES } from '@hcltech/shared/server-config/spec-category';
import { refreshKeepingFocus } from '../lib/keep-focus';
import { stockDetailHrefOf } from './StockDetailView';

export type InventoryTab = 'lines' | 'flows' | 'days' | 'units' | 'events';   // 품목별(products)은 2026-10-26 구매 건에 합쳐 퇴역 · flows = 변동표(기간 요약)(2026-10-20) · days = 일별(2026-10-21)

interface InventoryData {
  tab: InventoryTab;
  lineQuery: StockLineListQuery;    // 구매 건 — 찾기 · 제품 · 페이지 시작
  bLinePeriodOpen: boolean;         // 목록 탭의 구매일 칸이 펼쳐져 있나 — 처음엔 접힘(회의 2026-10-28 34:48 "필터를 열면 그제서야 날짜")
  lines: StockLineViewList | null;  // 구매 건 묶음. null = 아직
  flowQuery: StockFlowListQuery;         // 변동표 — 기간 둘(필수) · 찾기 · 움직인 것만 · 페이지 시작(2026-10-20)
  flows: StockFlowViewList | null;       // 변동표 묶음. null = 아직
  dayQuery: StockFlowDayQuery;           // 일별 — 고른 제품 · 컨디션(product_id '' = 아직 안 고름) · 기간 둘(2026-10-21 A 안 — 탭이 혼자 선다)
  dayPick: DayPick;                      // 일별의 고른 제품 · 그 제품의 컨디션 후보
  days: StockFlowDayList | null;         // 일별 묶음. null = 아직 · 안 고름
  query: UnitListQuery;             // 실물 — 상태 · 검색 · 나간 장 보이기 · 페이지 시작 — 서버가 거른다
  list: UnitViewList | null;        // 실물 묶음. null = 아직
  loading: boolean;
  error: string;
  bPicking: boolean;                // 고르기 모드 — [작업] 을 누르면 체크 칸이 나온다, [끝내기] 로 닫는다(2026-11-03)
  checked: Set<string>;             // 실물 id(BIGINT 라 글). 고르기 모드에서만 찬다
  actionNote: string | null;        // 메모 창(홀딩 · 자산)의 글. null = 창 닫힘(2026-10-20 — 칸을 미리 깔지 않고 [홀딩] 을 누르면 창이 뜬다)
  noteAction: ENoteAction;          // 메모 창이 무엇의 창인가 — 홀딩 · 자산으로(2026-10-29). 창이 닫혀 있으면 마지막 값 그대로
  acting: boolean;                  // 작업(홀딩 · 풀기 · 자산) 보내는 중 — 버튼 잠금
  menuOpen: boolean;                // [작업 ▾] 메뉴가 열려 있나
  actionError: string;              // 작업(홀딩 · 풀기 · 자산)에서 안 된 장 목록 글. '' 이면 없음(loadUnits 의 error 와 따로 — 그건 불러오기 실패)
  retire: RetireDraft | null;       // [사용 불가] 창 — 옮길 장 · 사유 · 글. null = 닫힘(재고 조정 2026-11-03)
  eventQuery: UnitEventListQuery;   // 변동표 — 기간 · 찾기 · 종류 · 사유 · 제품 · 업로드 · 페이지 시작(2026-10-20 실데이터)
  events: UnitEventLineViewList | null;   // 변동표 묶음. null = 아직
}

const QUERY_DEBOUNCE_MS = 300;      // 글자마다 서버를 안 부른다

/** 일별의 고른 제품 — 변동표 줄에서 들고 온 제품(칩에 보일 글) · 그 제품의 컨디션 후보(변동표 줄에서 읽는다 — 실물이 있는 컨디션만). 자동완성은 2026-10-26 퇴역(변동표 줄로만 연다) */
type DayPick = {
  product: { hclt_number: string; option_part: string | null; description: string | null } | null;   // 고른 제품. null = 아직
  conditions: string[];                                          // 고른 제품의 컨디션 후보. 고르기 전엔 []
};

/** 실물 탭의 작업 — [작업 ▾] 메뉴 한 줄씩. 순서가 메뉴 순서(2026-10-20 — 버튼 다섯을 하나로, 현민 "작업으로 모으죠") */
const E_UNIT_ACTIONS = ['inspect', 'move', 'hold', 'release', 'asset', 'asset_return', 'retire', 'restore'] as const;   // retire · restore 재고 조정(2026-11-03 — 옛 시안 lost 대신)
type EUnitAction = typeof E_UNIT_ACTIONS[number];
const UNIT_ACTION_WORD: Record<EUnitAction, string> = { inspect: '검수 완료', move: '이동', hold: '홀딩', release: '풀기', asset: '자산으로', asset_return: '자산 회수', retire: '사용 불가', restore: '다시 찾음' };
/** 메뉴 줄의 설명 한 줄 — 그 작업이 무엇을 하나(2026-11-03 "메뉴 설명도") */
const UNIT_ACTION_NOTE: Record<EUnitAction, string> = {
  inspect: '검수 중 → 가용',
  move: '창고 · 구역을 옮김',
  hold: '가용 수에서 잠시 뺌 — 메모 한 줄',
  release: '홀딩을 풀어 다시 가용',
  asset: '내부에서 쓰는 장으로 — 메모 한 줄',
  asset_return: '자산 → 검수 중',
  retire: '분실 · 파손 · 폐기 · 기타로 뺌',
  restore: '사용 불가 → 검수 중',
};
/** 아직 동작이 없는 작업(검수 완료 · 이동)의 까닭 글 — 메뉴에 보이되 잠김(현민 2026-11-03 "보이긴 하되 누르지 못하게") */
const NOT_BUILT_WORD = '준비 중';
const HOLDABLE_STATUSES: readonly EStockStatus[] = ['inspecting', 'available'];   // 서버 CHECK 와 같은 둘
/** 메모 한 줄을 받는 작업 — 같은 창을 쓴다(2026-10-29) */
type ENoteAction = Extract<EUnitAction, 'hold' | 'asset'>;
const NOTE_DIALOG_TEXT: Record<ENoteAction, { note: string; placeholder: string }> = {
  hold:  { note: '상태는 그대로 두고 잡아 둡니다 — 가용 수에서만 빠집니다. 풀면 다시 가용.', placeholder: '왜 잡아 두나 — 한 줄' },
  asset: { note: '내부에서 쓰는 장으로 돌립니다 — 가용 · 판매 배정에서 빠지고 보유에는 남습니다. [자산 회수] 로 검수 중에 돌아옵니다.', placeholder: '누가 · 어디서 쓰나 — 한 줄' },
};

/** 고른 실물들에 그 작업이 되나 — null 이면 된다, 글이면 안 되는 까닭(메뉴 줄이 회색 + 호버). 전부에 돼야 켜진다 */
function unitActionBlockOf(action: EUnitAction, units: readonly UnitView[]): string | null {
  switch (action) {
    case 'inspect':
    case 'move':    return NOT_BUILT_WORD;
    case 'hold':
      if (!units.every((u) => HOLDABLE_STATUSES.includes(u.stock_status))) return '검수 중 · 가용만';
      return units.every((u) => !u.is_held) ? null : '이미 홀딩인 실물이 있음';
    case 'release': return units.every((u) => u.is_held) ? null : '홀딩 중인 실물만';
    case 'asset':
      if (!units.every((u) => u.stock_status === 'available')) return '가용만';
      return units.every((u) => !u.is_held) ? null : '홀딩을 먼저 풉니다';
    case 'asset_return': return units.every((u) => u.stock_status === 'asset') ? null : '자산인 실물만';
    case 'retire': {
      const blocks = units.map(retireBlockOf).filter((block): block is string => block !== null);
      return blocks.length === 0 ? null : blocks[0];
    }
    case 'restore': return units.every((u) => u.stock_status === 'retired') ? null : '사용 불가(retired)인 실물만';
    default: {
      const never: never = action;
      throw new Error(`unhandled EUnitAction: ${String(never)}`);
    }
  }
}
/** [구매일] 버튼 글 — 기간이 없으면 "구매일 전체", 있으면 그 기간(접혀 있어도 걸린 줄 보이게) */
function linePeriodLabelOf(lineQuery: StockLineListQuery): string {
  if (lineQuery.from === '' && lineQuery.to === '') return '구매일 전체';
  return `구매일 ${lineQuery.from === '' ? '…' : lineQuery.from.slice(5)} ~ ${lineQuery.to === '' ? '…' : lineQuery.to.slice(5)}`;
}

/** 로트 수량 넷의 머리 · 칸 — 재고 목록과 재고 상세 입고 절이 같이 쓴다. 전부 그 로트 하나의 수, 입고 = 출고 + 사용 중 + 사용 가능(2026-10-29 · 회의 2026-10-28) */
export const LOT_COUNT_HEAD = `<th title="구매 수량(구매 기록 없는 줄은 실물 수)">입고</th><th title="입고 − 사용 중 − 사용 가능 — 이카운트 옮기기 전에 나간 장도 든다">출고</th>`
  + `<th title="남은 장 중 사용 가능이 아닌 것 — 검수 중 · 예약 · 임대 · 자산 · 사용 불가 · 홀딩">사용 중</th><th title="가용 · 홀딩 아님 — 바로 팔 수 있는 수">사용 가능</th>`;
/** 0 은 흐리게, 사용 중은 빨강 · 사용 가능은 초록(회의 2026-10-28 31:51) */
export function lotCountCells(line: StockLineView): string {
  const cell = (count: number, tone: string): string => `<td class="pl-main pl-qty${count === 0 ? ' is-zero' : tone}">${count}</td>`;
  return cell(line.inbound_count, '') + cell(line.outbound_count, '') + cell(line.in_use_count, ' is-in-use') + cell(line.usable_count, ' is-usable');
}
/** 상태 필터에 고를 수 있는 것 — 전체는 보유(나간 장 빼고), 출고 · 이탈은 각각 그것만 */
const FILTER_STATUSES: readonly EStockStatus[] = ['inspecting', 'available', 'reserved', 'rented', 'asset', 'dispatched', 'retired'];   // 출고 · 이탈은 따로 고른다(현민 2026-10-20) — 고르면 show_gone 을 켠다
const UNITS_HREF = '/preview/inventory/units';
const EVENTS_HREF = '/preview/inventory/events';
const FLOWS_HREF = '/preview/inventory/flows';
const DAYS_HREF = '/preview/inventory/flows/days';   // 변동표 메뉴 밑 — 사이드바가 변동표를 켠다(findMenuPlace 의 아래 경로)
const DEFAULT_EVENT_DAYS = 30;   // 변동표 첫 기간 — 오늘부터 30일 전(KST)
type PeriodTab = 'lines' | 'flows' | 'days' | 'events';   // 기간 칸이 있는 탭 — 목록(구매일, 2026-10-28) · 변동표 · 일별 · 이력
const PERIOD_TABS: readonly PeriodTab[] = ['lines', 'flows', 'days', 'events'];
/** 기간 프리셋 — 시작 = 오늘에서 days 일 전, 끝 = 오늘(KST). 1개월은 달력이 아니라 30일(회의 2026-10-21 §13 · 현민 2026-10-26) */
const PERIOD_PRESETS: readonly { label: string; days: number }[] = [
  { label: '오늘', days: 0 },
  { label: '1주', days: 7 },
  { label: '1개월', days: 30 },
  { label: '3개월', days: 90 },
  { label: '1년', days: 365 },
];

/** 변동표 한 줄의 칩 글 + 내용 글 — 서버가 재고가 움직인 줄만 주니(상태 전이 + parent · condition · is_held) 그 넷을 사람 말로(2026-10-20) */
function eventWordsOf(event: UnitEventLineView): { chip: string; detail: string } {
  const old = event.old_value;
  const now = event.new_value;
  if (event.type === 'created') {
    const from = event.stocktake_id === null ? '' : ` · 이카운트 재고현황 반영 #${event.stocktake_id}`;
    return { chip: EVENT_WORD.created, detail: `${ORIGIN_WORD[event.origin]}${from}` };
  }
  if (event.type === 'reserved' || event.type === 'sale_step') {   // 판매에 잡힘 · 풀림 · 판매의 단계(출고 요청 · 세팅 완료, 367) — 묶인 견적 번호를 붙인다
    const quote = event.quote_no === null ? '' : ` · ${event.quote_no}`;
    return { chip: eventWordOf(event.type, now), detail: `${valueWordOf(event.column_name, old)} → ${valueWordOf(event.column_name, now)}${quote}` };
  }
  if (event.type !== 'changed') {
    const detail = event.column_name === null ? '' : `${valueWordOf(event.column_name, old)} → ${valueWordOf(event.column_name, now)}`;
    return { chip: EVENT_WORD[event.type], detail };
  }
  switch (event.column_name) {
    case 'parent_unit_id': return now === null ? { chip: '서버에서 뺌', detail: `서버 #${old ?? '?'} 에서` } : { chip: '서버에 넣음', detail: `서버 #${now} 에` };
    case 'condition':      return { chip: '컨디션', detail: `${old ?? '—'} → ${now ?? '—'}` };
    case 'is_held':        return now === 'true' ? { chip: '홀딩', detail: '' } : { chip: '홀딩 풀림', detail: '' };
    default:               return { chip: EVENT_WORD.changed, detail: `${event.column_name ?? ''} ${old ?? '—'} → ${now ?? '—'}` };   // 서버가 안 주는 열 — 혹시 오면 날것 그대로
  }
}

/** 오늘(KST)에서 days 일 전의 'YYYY-MM-DD' */
function kstDateOf(daysAgo: number): string {
  const now = new Date(Date.now() - daysAgo * 86_400_000);
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}
/** 이달 1일(KST)의 'YYYY-MM-DD' — 변동표 기본 시작 */
function kstMonthStartOf(): string {
  return `${kstDateOf(0).slice(0, 7)}-01`;
}
/** 일별 탭의 주소 — 고른 제품 · 컨디션 · 기간을 남긴다(새로고침 · 공유). 안 골랐으면 탭 주소만 */
function dayHrefOf(dayQuery: StockFlowDayQuery): string {
  if (dayQuery.product_id === '') return DAYS_HREF;
  const params = new URLSearchParams({ product_id: dayQuery.product_id, condition: dayQuery.condition, from: dayQuery.from, to: dayQuery.to });
  return `${DAYS_HREF}?${params.toString()}`;
}
/** 변동표의 주소 — 일별의 [← 변동표] 가 같은 기간으로 돌아간다 */
function flowHrefOf(from: string, to: string): string {
  return `${FLOWS_HREF}?${new URLSearchParams({ from, to }).toString()}`;
}
/** 일별 한 줄 — 날짜 · 기초재고 · 입고 · 출고 · 기말재고. 입고 · 출고 0 은 회색, 재고는 굵게(변동표 줄과 같은 톤) */
function renderStockFlowDayRow(line: StockFlowDayView, index: number): string {
  const qty = (n: number, cls: string): string => `<td class="pl-qty ${cls}">${n === 0 ? '<span class="text-muted">0</span>' : n}</td>`;
  const bMoved = line.inbound > 0 || line.outbound > 0;
  return `
    <tr${bMoved ? '' : ' class="pv-day-quiet"'}>
      ${rowNoCell(index, 0)}
      <td class="pl-main mono">${escapeHtml(line.date)}</td>
      ${qty(line.opening, 'pl-sub')}
      ${qty(line.inbound, 'pl-sub')}
      ${qty(line.outbound, 'pl-sub')}
      ${qty(line.closing, 'pl-main')}
    </tr>`;
}
export const STOCK_LINE_SOURCE_WORD: Record<EStockLineSource, string> = { ecount: '이카운트', po: '구매', none: '기록 없음' };
const priceCell = (line: StockLineView): string => costTextOf(line.unit_price, line.cost_currency);
/** 글 칸 — null 이면 "—", 아니면 escape 한 글(2026-10-26) */
const textOrDash = (text: string | null): string => (text === null ? '—' : escapeHtml(text));


export class InventoryView extends SolaView<InventoryData> {
  override events = {
    // sola 는 처음 맞는 셀렉터 하나만 부른다 — 줄 안의 버튼을 줄보다 먼저
    click:  { '@period-preset': 'handlePeriodPreset', '@check': 'handleCheck', '@unit-row': 'handleOpenUnit', '@pick-start': 'handlePickStart', '@pick-end': 'handlePickEnd', '@menu-toggle': 'handleMenuToggle', '@menu-item': 'handleMenuItem', '@note-cancel': 'handleNoteCancel', '@note-confirm': 'handleNoteConfirm', '@release': 'handleRelease', '@retire-cancel': 'handleRetireCancel', '@retire-confirm': 'handleRetireConfirm', '@page': 'handlePage', '@clear-product': 'handleClearProduct',
              '@line-page': 'handleLinePage', '@line-period-all': 'handleLinePeriodAll', '@line-period-toggle': 'handleLinePeriodToggle', '@line': 'handleOpenLine',
              '@flow-pn': 'handleOpenFlowPn', '@flow-page': 'handleFlowPage', '@flow-row': 'handleOpenFlowRow',   // 파트넘버 버튼이 줄보다 앞 — Sola 는 첫 매칭만
              '@day-pn': 'handleOpenDayPn',
              '@event-pn': 'handleOpenEventPn', '@event-page': 'handleEventPage', '@event-clear-product': 'handleEventClearProduct', '@event-clear-stocktake': 'handleEventClearStocktake' },
    change: { '@status-filter': 'handleStatusFilter', '@retire-reason': 'handleRetireReason', '@line-from': 'handleLineFrom', '@line-to': 'handleLineTo', '@line-category': 'handleLineCategory', '@flow-from': 'handleFlowFrom', '@flow-to': 'handleFlowTo', '@flow-only-moved': 'handleFlowOnlyMoved', '@day-from': 'handleDayFrom', '@day-to': 'handleDayTo', '@day-condition': 'handleDayCondition', '@event-from': 'handleEventFrom', '@event-to': 'handleEventTo', '@event-type': 'handleEventType', '@event-reason': 'handleEventReason' },
    input:  { '@action-note': 'handleActionNote', '@retire-note': 'handleRetireNote', '@query': 'handleQuery', '@event-query': 'handleEventQuery', '@line-query': 'handleLineQuery', '@flow-query': 'handleFlowQuery' },
  };

  private readonly drawer = new DetailDrawer();
  private queryTimer: ReturnType<typeof setTimeout> | null = null;
  private loadSeq = 0;   // 불러오기 순번 — 늦게 온 옛 응답이 새 결과를 덮지 않게(QA 2026-10-20 #3 응답 경합)

  /** product_id '' = 전체. 제품 상세의 링크(?product_id=)로 열면 그 실물만 */
  constructor(params: { q: string; tab: InventoryTab; product_id: string; stocktake_id: string; condition: string; from: string; to: string }) {   // q 는 이력을 찾기 글(?q=)로 열 때 · stocktake_id 는 변동표를 "이 업로드가 바꾼 것" 으로 열 때 · condition · from · to 는 일별(제품 × 컨디션 · 변동표의 그 기간)을 열 때('' = 기본 기간)
    super();
    const eventQuery: UnitEventListQuery = { q: params.q, types: [], reason: '', from: params.stocktake_id === '' ? kstDateOf(DEFAULT_EVENT_DAYS) : '', to: '', product_id: params.product_id, stocktake_id: params.stocktake_id, offset: 0 };   // 처음엔 최근 30일. 업로드로 열면 기간 없이 전부
    const lineQuery: StockLineListQuery = { q: '', product_id: '', include_gone: false, category: '', from: '', to: '', offset: 0 };
    const flowQuery: StockFlowListQuery = { q: '', from: params.tab === 'flows' && params.from !== '' ? params.from : kstMonthStartOf(), to: params.tab === 'flows' && params.to !== '' ? params.to : kstDateOf(0), only_moved: false, offset: 0 };   // 처음엔 이달 1일 ~ 오늘(KST). 일별의 [← 변동표] 로 오면 그 기간(?from=&to=)
    const dayQuery: StockFlowDayQuery = { product_id: params.product_id, condition: params.condition, from: params.from === '' ? kstMonthStartOf() : params.from, to: params.to === '' ? kstDateOf(0) : params.to };   // 주소(?product_id=&condition=)로 열면 제품 · 컨디션이 차 있다. 기간은 변동표와 같은 기본
    const query: UnitListQuery = { status: '', q: '', show_gone: false, product_id: params.product_id, offset: 0 };
    const dayPick: DayPick = { product: null, conditions: [] };
    this.data = { tab: params.tab, lineQuery, bLinePeriodOpen: false, lines: null, flowQuery, flows: null, dayQuery, dayPick, days: null, query, list: null, loading: true, error: '', bPicking: false, checked: new Set(), actionNote: null, noteAction: 'hold', acting: false, menuOpen: false, actionError: '', retire: null, eventQuery, events: null };
  }

  override getTitle(): string {
    if (this.data.tab === 'events') return '재고 이력 — HCLTech';
    if (this.data.tab === 'flows') return '재고 변동표 — HCLTech';
    if (this.data.tab === 'days') return '재고 일별 — HCLTech';
    return '재고 현황 — HCLTech';
  }

  override onMount(): void {
    if (this.data.tab === 'lines') void this.loadLines();
    if (this.data.tab === 'flows') void this.loadFlows();
    if (this.data.tab === 'days') void this.openDaysFromUrl();
    if (this.data.tab === 'events') void this.loadEvents();
    if (this.data.tab === 'units') void this.loadUnits();
  }

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <div class="page page-inventory pv-page">
        <header class="page-header">${this.renderHeading()}</header>
        <section class="page-body pv-body">${this.renderTabBody()}</section>
        ${this.drawer}
      </div>`;
  }

  // ─── 탭 ───
  /** 머리글 — 변동표 · 일별은 따로 선 메뉴라 탭 줄 없이 제목만(회의 2026-10-21 §13), 나머지는 재고 현황 + 탭 셋 */
  private renderHeading(): string {
    const bFlowMenu = this.data.tab === 'flows' || this.data.tab === 'days';
    if (bFlowMenu) return '<h1>📦 재고 변동표</h1>';
    return `<h1>📦 재고 현황</h1>${this.renderTabs()}`;
  }

  private renderTabs(): string {
    const tab = (key: InventoryTab, href: string, label: string): string => `<a class="ic-tab${this.data.tab === key ? ' active' : ''}" data-link href="${href}">${label}</a>`;
    return `<nav class="ic-tabs">${tab('lines', '/preview/inventory', '목록')}${tab('units', UNITS_HREF, '실물')}${tab('events', EVENTS_HREF, '이력')}</nav>`;
  }

  /** 탭에 맞는 본문 */
  private renderTabBody(): string {
    if (this.data.tab === 'lines') return this.renderLines();
    if (this.data.tab === 'flows') return this.renderFlows();
    if (this.data.tab === 'days') return this.renderDays();
    if (this.data.tab === 'units') return this.renderUnits();
    return this.renderEvents();
  }

  // ─── 구매 건 ───
  /** 지금 lineQuery 로 한 묶음. 실패면 error 글. 끝나면 본문 다시 그림 */
  /** 본문(.pv-body)을 다시 그린다 — 찾기 칸에 치던 포커스 · 커서는 지킨다(lib/keep-focus) */
  private refreshBody(render: () => string): void {
    refreshKeepingFocus(this._rootElement ?? null, '.pv-body', () => this.refreshRegion('.pv-body', render));
  }

  private async loadLines(): Promise<void> {
    const seq = ++this.loadSeq;
    this.data = { ...this.data, loading: true, error: '' };
    this.refreshBody(() => this.renderLines());
    try {
      const lines = await api.fetchStockLines(this.data.lineQuery);
      if (seq !== this.loadSeq) return;   // 그 사이 새 불러오기가 시작됐다 — 이 응답은 버린다
      this.data = { ...this.data, lines, loading: false };
    } catch (e) {
      if (seq !== this.loadSeq) return;
      this.data = { ...this.data, loading: false, error: e instanceof Error ? e.message : String(e) };
    }
    this.refreshBody(() => this.renderLines());
  }

  private renderLines(): string {
    const { lineQuery, lines, loading, error } = this.data;
    const rows = lines === null ? [] : lines.rows;
    const emptyWord = loading ? '불러오는 중…' : error !== '' ? `불러오지 못했습니다 — ${escapeHtml(error)}` : '맞는 줄이 없습니다';
    const body = rows.length === 0 ? `<tr><td colspan="15" class="text-muted">${emptyWord}</td></tr>` : rows.map((line, index) => this.renderStockLineRow(line, lineQuery.offset + index)).join('');

    return `${this.renderLineToolbar(lineQuery, lines)}
      <table class="pv-table pv-lines">
        <thead><tr>${ROW_NO_HEAD}<th>파트넘버</th><th>디스크립션</th><th>카테고리</th><th>제조사</th><th>컨디션</th>${LOT_COUNT_HEAD}
          <th>단가</th><th>구매일</th><th>구매처</th><th>담당자</th><th title="출처 칩 + 이카운트 전표번호 · PO 번호">구매번호</th></tr></thead>
        <tbody>${body}</tbody>
      </table>`;
  }

  /** 목록 도구 줄 — 찾기(맨 앞, 총괄 정리 2026-10-30) · 구매일 기간(프리셋 · 전체) · 카테고리 · 페이징 */
  private renderLineToolbar(lineQuery: StockLineListQuery, lines: StockLineViewList | null): string {
    const bAllPeriod = lineQuery.from === '' && lineQuery.to === '';
    const categories = SPEC_CATEGORIES.map((category) => `<option value="${category}" ${category === lineQuery.category ? 'selected' : ''}>${category}</option>`).join('');
    const periodRow = !this.data.bLinePeriodOpen ? '' : `
        <div class="pv-period-row">
          <label class="pc-date" title="구매일"><input type="date" data-action="line-from" value="${escapeHtml(lineQuery.from)}"> ~ <input type="date" data-action="line-to" value="${escapeHtml(lineQuery.to)}"></label>
          <span class="pc-presets"><button type="button" class="pc-preset${bAllPeriod ? ' is-on' : ''}" data-action="line-period-all">전체</button></span>
          ${this.renderPeriodPresets('lines', lineQuery.from, lineQuery.to)}
        </div>`;
    return `
      <div class="pc-filter-bar">
        <label class="pc-search">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
          <input type="search" data-action="line-query" placeholder="파트넘버 · 디스크립션 · 구매처" value="${escapeHtml(lineQuery.q)}">
        </label>
        <button type="button" class="pc-select pv-period-toggle${bAllPeriod ? '' : ' is-on'}${this.data.bLinePeriodOpen ? ' is-open' : ''}" data-action="line-period-toggle" aria-expanded="${this.data.bLinePeriodOpen}">${linePeriodLabelOf(lineQuery)}</button>
        <select class="pc-select" data-action="line-category" aria-label="카테고리"><option value="">전체 카테고리</option>${categories}</select>
        <span class="pv-spacer"></span>
        ${lines === null ? '' : renderPagerLine(lines, 'line-page')}
        ${periodRow}
      </div>`;
  }

  /** 구매 줄 한 줄 — 누르면 재고 상세 새 탭(@line). 파트넘버는 굵은 글(줄이 받는다) */
  private renderStockLineRow(line: StockLineView, no: number): string {
    const pn = escapeHtml(pnTextOf(line));

    return `
      <tr class="pl-row" data-action="line" data-id="${line.hclt_product_id}" data-condition="${escapeHtml(line.condition)}" data-line-key="${escapeHtml(line.line_key)}">
        ${rowNoCell(no, 0)}
        <td class="pl-main mono"><b>${pn}</b></td>
        <td class="pl-main pl-desc"><div class="pl-clamp">${line.description === null ? '' : escapeHtml(line.description)}</div></td>
        <td class="pl-sub">${textOrDash(line.spec_category)}</td>
        <td class="pl-sub">${textOrDash(line.vendor)}</td>
        <td class="pl-sub">${escapeHtml(line.condition)}</td>
        ${lotCountCells(line)}
        <td class="pl-sub mono">${priceCell(line)}</td>
        <td class="pl-sub mono">${line.purchased_date ?? '—'}</td>
        <td class="pl-sub pl-supplier" title="${line.supplier_name === null ? '' : escapeHtml(line.supplier_name)}">${textOrDash(line.supplier_name)}</td>
        <td class="pl-sub">${textOrDash(line.buyer_name)}</td>
        <td class="pl-sub mono"><span class="chip pv-source">${STOCK_LINE_SOURCE_WORD[line.source]}</span> ${line.purchase_no === null ? '' : escapeHtml(line.purchase_no)}</td>
      </tr>`;
  }

  // ─── 불러오기 ───
  /** 지금 query 로 한 묶음. 실패면 error 글. 끝나면 본문 다시 그림 */
  private async loadUnits(): Promise<void> {
    const seq = ++this.loadSeq;
    this.data = { ...this.data, loading: true, error: '' };
    this.refreshBody(() => this.renderUnits());
    try {
      const list = await api.fetchUnits(this.data.query);
      if (seq !== this.loadSeq) return;
      this.data = { ...this.data, list, loading: false };
    } catch (e) {
      if (seq !== this.loadSeq) return;
      this.data = { ...this.data, loading: false, error: e instanceof Error ? e.message : String(e) };
    }
    this.refreshBody(() => this.renderUnits());
  }

  // ─── 현황 ───
  private renderUnits(): string {
    const { query, list, loading, error, checked, bPicking } = this.data;
    const rows = list === null ? [] : list.rows;
    const emptyWord = loading ? '불러오는 중…' : error !== '' ? `불러오지 못했습니다 — ${escapeHtml(error)}` : '맞는 줄이 없습니다';
    return `
      <div class="pc-filter-bar">
        <label class="pc-search">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
          <input type="search" data-action="query" placeholder="파트넘버 · 시리얼 · 실물 번호 · 품목코드 · 품목" value="${escapeHtml(query.q)}">
        </label>
        <select class="pc-select" data-action="status-filter" aria-label="상태">
          <option value="">전체 상태</option>
          ${FILTER_STATUSES.map((status) => `<option value="${status}" ${query.status === status ? 'selected' : ''}>${STATUS_WORD[status]}</option>`).join('')}
        </select>
        ${query.product_id === '' ? '' : `<span class="pv-product-chip">제품 #${escapeHtml(query.product_id)} 의 실물만 <button type="button" data-action="clear-product">전체 보기</button></span>`}
        <span class="pv-spacer"></span>
        ${list === null ? '' : renderPagerLine(list, 'page')}
        ${this.renderPickBar(rows)}
      </div>
      ${this.data.actionError === '' ? '' : `<p class="pv-action-error">${escapeHtml(this.data.actionError)}</p>`}
      ${this.renderNoteDialog()}
      ${this.data.retire === null ? '' : renderRetireDialog(this.data.retire, this.data.acting)}
      ${renderUnitTable(rows, bPicking ? { kind: 'pick', checked } : { kind: 'browse' }, emptyWord, query.offset)}`;
  }

  // ─── 변동표(기간 요약) ───
  /** 지금 flowQuery 로 한 묶음. 실패면 error 글. 끝나면 본문 다시 그림 */
  private async loadFlows(): Promise<void> {
    const seq = ++this.loadSeq;
    this.data = { ...this.data, loading: true, error: '' };
    this.refreshBody(() => this.renderFlows());
    try {
      const flows = await api.fetchStockFlows(this.data.flowQuery);
      if (seq !== this.loadSeq) return;
      this.data = { ...this.data, flows, loading: false };
    } catch (e) {
      if (seq !== this.loadSeq) return;
      this.data = { ...this.data, loading: false, error: e instanceof Error ? e.message : String(e) };
    }
    this.refreshBody(() => this.renderFlows());
  }

  private renderFlows(): string {
    const { flowQuery, flows, loading, error } = this.data;
    const rows = flows === null ? [] : flows.rows;
    const emptyWord = loading ? '불러오는 중…' : error !== '' ? `불러오지 못했습니다 — ${escapeHtml(error)}` : '맞는 줄이 없습니다';
    const body = rows.length === 0 ? `<tr><td colspan="8" class="text-muted">${emptyWord}</td></tr>` : rows.map((line, index) => this.renderStockFlowRow(line, flowQuery.offset + index)).join('');

    return `${this.renderFlowToolbar(flowQuery, flows)}
      <p class="pv-lead">제품 × 컨디션 하나 = 한 줄. 기초재고 + 입고 − 출고 = 기말재고. 입고 = 입고 · 반품(자산 회수는 빼고), 출고 = 출고 · 임대 · 이탈. 서버 속 부품도 센다 — 품목별 보유보다 클 수 있다.</p>
      <table class="pv-table pv-stock-flows">
        <thead><tr>${ROW_NO_HEAD}<th>파트넘버</th><th>품목</th><th>컨디션</th><th>기초재고</th><th>입고</th><th>출고</th><th>기말재고</th></tr></thead>
        <tbody>${body}</tbody>
      </table>`;
  }

  /** 변동표 도구 줄 — 기간 둘(필수) · 찾기 · 움직인 것만 · 페이징 */
  private renderFlowToolbar(flowQuery: StockFlowListQuery, flows: StockFlowViewList | null): string {
    return `
      <div class="pc-filter-bar">
        <label class="pc-date"><input type="date" data-action="flow-from" value="${escapeHtml(flowQuery.from)}" required> ~ <input type="date" data-action="flow-to" value="${escapeHtml(flowQuery.to)}" required></label>
        ${this.renderPeriodPresets('flows', flowQuery.from, flowQuery.to)}
        <label class="pc-search">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
          <input type="search" data-action="flow-query" placeholder="파트넘버 · 품목 · 제품번호" value="${escapeHtml(flowQuery.q)}">
        </label>
        <label class="pc-check"><input type="checkbox" data-action="flow-only-moved" ${flowQuery.only_moved ? 'checked' : ''}> 움직인 것만</label>
        <span class="pv-spacer"></span>
        ${flows === null ? '' : renderPagerLine(flows, 'flow-page')}
      </div>`;
  }

  /** 변동표 한 줄 — 줄을 누르면 그 제품 × 컨디션의 일별(같은 기간), 파트넘버는 재고 상세 새 탭. 입고 · 출고 0 은 회색, 재고는 굵게 */
  private renderStockFlowRow(line: StockFlowView, no: number): string {
    const qty = (n: number, cls: string): string => `<td class="pl-qty ${cls}">${n === 0 ? '<span class="text-muted">0</span>' : n}</td>`;
    return `
      <tr class="pl-row" data-action="flow-row" data-id="${line.hclt_product_id}" data-condition="${escapeHtml(line.condition)}" title="누르면 이 제품 × 컨디션의 일별">
        ${rowNoCell(no, 0)}
        <td class="pl-main"><button type="button" class="pv-pn mono" data-action="flow-pn" data-id="${line.hclt_product_id}"><b>${escapeHtml(pnTextOf(line))}</b></button></td>
        <td class="pl-main pl-desc"><div class="pl-clamp">${line.description === null ? '' : escapeHtml(line.description)}</div></td>
        <td class="pl-sub">${escapeHtml(line.condition)}</td>
        ${qty(line.opening, 'pl-sub')}
        ${qty(line.inbound, 'pl-sub')}
        ${qty(line.outbound, 'pl-sub')}
        ${qty(line.closing, 'pl-main')}
      </tr>`;
  }

  /** 기간 한쪽을 바꾼다 — from > to 가 되면 다른 쪽을 같은 날로 당긴다(서버는 400). 빈 값은 무시 */
  private setFlowPeriod(side: 'from' | 'to', value: string): void {
    if (value === '') return;
    const current = this.data.flowQuery;
    const from = side === 'from' ? value : current.from;
    const to = side === 'to' ? value : current.to;
    const bFlipped = from > to;   // 'YYYY-MM-DD' 는 글 순서가 날짜 순서
    this.data = { ...this.data, flowQuery: { ...current, from: bFlipped && side === 'to' ? to : from, to: bFlipped && side === 'from' ? from : to, offset: 0 } };
    void this.loadFlows();
  }

  handleFlowFrom(_e: Event, el: HTMLElement): void { this.setFlowPeriod('from', (el as HTMLInputElement).value); }
  handleFlowTo(_e: Event, el: HTMLElement): void { this.setFlowPeriod('to', (el as HTMLInputElement).value); }

  handleFlowQuery(_e: Event, el: HTMLElement): void {
    this.data = { ...this.data, flowQuery: { ...this.data.flowQuery, q: (el as HTMLInputElement).value, offset: 0 } };
    if (this.queryTimer !== null) clearTimeout(this.queryTimer);
    this.queryTimer = setTimeout(() => { this.queryTimer = null; void this.loadFlows(); }, QUERY_DEBOUNCE_MS);
  }

  handleFlowOnlyMoved(_e: Event, el: HTMLElement): void {
    this.data = { ...this.data, flowQuery: { ...this.data.flowQuery, only_moved: (el as HTMLInputElement).checked, offset: 0 } };
    void this.loadFlows();
  }

  handleFlowPage(_e: Event, el: HTMLElement): void {
    const offset = Number(el.dataset.offset);
    assert(Number.isInteger(offset) && offset >= 0, '페이저 버튼은 data-offset(그 쪽의 offset)을 가진다 — renderPagerLine 이 붙였다');
    this.data = { ...this.data, flowQuery: { ...this.data.flowQuery, offset } };
    void this.loadFlows();
  }

  /** 변동표 줄 — 그 제품 × 컨디션의 일별로(변동표의 기간을 들고 간다. 회의 2026-10-21 55:57 "상세를 누르면 일별 페이지로") */
  handleOpenFlowRow(_e: Event, el: HTMLElement): void {
    if (hasTextSelection()) return;   // 긁어 복사(드래그)면 안 연다 — 클릭과 가른다(2026-10-30 전수)
    const productId = el.dataset.id ?? '';
    const condition = el.dataset.condition ?? '';
    assert(productId !== '' && condition !== '', '변동표 줄은 data-id(제품) · data-condition 을 가진다 — renderStockFlowRow 가 붙였다');
    const { from, to } = this.data.flowQuery;
    navigate(dayHrefOf({ product_id: productId, condition, from, to }));
  }

  handleOpenFlowPn(_e: Event, el: HTMLElement): void {
    const id = el.dataset.id ?? '';
    assert(id !== '', '파트넘버 버튼은 data-id(hclt_product_id)를 가진다 — renderStockFlowRow 가 붙였다');
    window.open(stockDetailHrefOf(id, '', ''), '_blank', 'noopener');   // 재고 상세(2026-10-26 — 회의 §15)
  }

  // ─── 일별(제품 × 컨디션 하나 · 날짜 하나 = 한 줄) — 탭이 혼자 선다: 파트넘버 자동완성 → 컨디션 → 기간(A 안, 2026-10-21) ───
  /** 지금 dayQuery 로 한 묶음. 제품 · 컨디션을 안 골랐으면 부르지 않고 도구 줄만. 실패면 error 글. 주소에 질의를 남긴다 */
  private async loadDays(): Promise<void> {
    history.replaceState(null, '', dayHrefOf(this.data.dayQuery));
    const bPicked = this.data.dayQuery.product_id !== '' && this.data.dayQuery.condition !== '';
    if (!bPicked) {
      this.data = { ...this.data, days: null, loading: false, error: '' };
      this.refreshBody(() => this.renderDays());
      return;
    }
    const seq = ++this.loadSeq;
    this.data = { ...this.data, loading: true, error: '' };
    this.refreshBody(() => this.renderDays());
    try {
      const days = await api.fetchStockFlowDays(this.data.dayQuery);
      if (seq !== this.loadSeq) return;
      this.data = { ...this.data, days, loading: false };
    } catch (e) {
      if (seq !== this.loadSeq) return;
      this.data = { ...this.data, loading: false, error: e instanceof Error ? e.message : String(e) };
    }
    this.refreshBody(() => this.renderDays());
  }

  /** 주소로 열렸을 때(product_id 있음) — 고른 제품의 글 · 컨디션 후보를 채우고 나서 묶음을 부른다 */
  private async openDaysFromUrl(): Promise<void> {
    const { product_id } = this.data.dayQuery;
    if (product_id === '') { await this.loadDays(); return; }
    try {
      const detail = await api.fetchProduct(product_id);
      await this.pickDayProduct({ id: detail.id, hclt_number: detail.hclt_number, option_part: detail.option_part, description: detail.description }, this.data.dayQuery.condition);
    } catch (e) {
      this.data = { ...this.data, loading: false, error: e instanceof Error ? e.message : String(e) };
      this.refreshBody(() => this.renderDays());
    }
  }

  /** 제품 하나를 고른다 — 글은 파트넘버로, 컨디션 후보는 변동표 줄(그 제품 · 지금 기간)에서. 원하는 컨디션이 후보에 없으면 첫째 */
  private async pickDayProduct(product: { id: string; hclt_number: string; option_part: string | null; description: string | null }, wantedCondition: string): Promise<void> {
    const text = pnTextOf(product);
    this.data = { ...this.data, dayPick: { product, conditions: [] }, dayQuery: { ...this.data.dayQuery, product_id: product.id, condition: '' }, days: null, loading: true, error: '' };
    this.refreshBody(() => this.renderDays());
    const conditions = await this.fetchDayConditions(product.id, text);
    const condition = conditions.includes(wantedCondition) ? wantedCondition : (conditions[0] ?? '');
    this.data = { ...this.data, dayPick: { ...this.data.dayPick, conditions }, dayQuery: { ...this.data.dayQuery, condition } };
    await this.loadDays();
  }

  /** 그 제품의 컨디션 후보 — 변동표(같은 기간)를 그 제품 글로 찾아 제품 id 가 같은 줄의 컨디션만. 줄이 없으면 [](기간 안 사건도 실물도 없다) */
  private async fetchDayConditions(productId: string, text: string): Promise<string[]> {
    const { from, to } = this.data.dayQuery;
    const flows = await api.fetchStockFlows({ q: text, from, to, only_moved: false, offset: 0 });
    return flows.rows.filter((line) => line.hclt_product_id === productId).map((line) => line.condition);
  }

  private renderDays(): string {
    const { dayQuery, dayPick, days, loading, error } = this.data;
    const bPicked = dayQuery.product_id !== '' && dayQuery.condition !== '';
    const rows = days === null ? [] : days.rows;
    const guide = dayPick.product === null ? '변동표에서 줄을 누르면 그 제품 × 컨디션의 일별이 열립니다' : dayPick.conditions.length === 0 && !loading ? '이 제품은 이 기간에 실물도 사건도 없습니다' : '';
    const emptyWord = guide !== '' ? guide : loading ? '불러오는 중…' : error !== '' ? `불러오지 못했습니다 — ${escapeHtml(error)}` : '날짜 줄이 없습니다';
    const body = !bPicked || rows.length === 0 ? `<tr><td colspan="6" class="text-muted">${emptyWord}</td></tr>` : rows.map((line, index) => renderStockFlowDayRow(line, index)).join('');

    return `${this.renderDayToolbar(dayQuery, dayPick)}
      <p class="pv-lead">제품 × 컨디션 하나를 날짜 축으로 편 변동표. 날짜 하나 = 한 줄, 기초재고(그날 0시) + 입고 − 출고 = 기말재고(그날 끝). 사건 없는 날도 줄이 있다 — 셈은 변동표와 같다.</p>
      <table class="pv-table pv-stock-days">
        <thead><tr>${ROW_NO_HEAD}<th>날짜</th><th>기초재고</th><th>입고</th><th>출고</th><th>기말재고</th></tr></thead>
        <tbody>${body}</tbody>
      </table>`;
  }

  /** 일별 도구 줄 — 파트넘버 자동완성(칸 + 후보 listbox) · 고른 제품 칩(제품 상세 새 탭) · 컨디션 셀렉트(그 제품의 후보) · 기간 둘(필수) */
  private renderDayToolbar(dayQuery: StockFlowDayQuery, dayPick: DayPick): string {
    const back = `<a class="btn-ghost" data-link href="${escapeHtml(flowHrefOf(dayQuery.from, dayQuery.to))}">← 변동표</a>`;
    if (dayPick.product === null) return `<div class="pc-filter-bar">${back}</div>`;
    const chip = `<span class="pv-product-chip"><button type="button" class="pv-pn mono" data-action="day-pn" data-id="${dayQuery.product_id}"><b>${escapeHtml(pnTextOf(dayPick.product))}</b></button>${dayPick.product.description === null ? '' : `<span class="pl-desc">${escapeHtml(dayPick.product.description)}</span>`}</span>`;
    const conditions = dayPick.conditions.map((condition) => `<option value="${escapeHtml(condition)}" ${condition === dayQuery.condition ? 'selected' : ''}>${escapeHtml(condition)}</option>`).join('');
    return `
      <div class="pc-filter-bar">
        ${back}
        ${chip}
        <select class="pc-select" data-action="day-condition" aria-label="컨디션" ${dayPick.conditions.length === 0 ? 'disabled' : ''}>${conditions === '' ? '<option value="">컨디션</option>' : conditions}</select>
        <label class="pc-date"><input type="date" data-action="day-from" value="${escapeHtml(dayQuery.from)}" required> ~ <input type="date" data-action="day-to" value="${escapeHtml(dayQuery.to)}" required></label>
        ${this.renderPeriodPresets('days', dayQuery.from, dayQuery.to)}
        <span class="pv-spacer"></span>
      </div>`;
  }

  /** 기간 한쪽을 바꾼다 — 변동표의 setFlowPeriod 와 같은 규칙(from > to 면 다른 쪽을 당긴다). 366일 넘는 건 서버가 400 — 글로 보인다. 기간이 바뀌면 컨디션 후보도 다시 읽는다 */
  private setDayPeriod(side: 'from' | 'to', value: string): void {
    if (value === '') return;
    const current = this.data.dayQuery;
    const from = side === 'from' ? value : current.from;
    const to = side === 'to' ? value : current.to;
    const bFlipped = from > to;
    this.data = { ...this.data, dayQuery: { ...current, from: bFlipped && side === 'to' ? to : from, to: bFlipped && side === 'from' ? from : to } };
    this.reloadDayPeriod();
  }

  /** 일별 기간이 바뀐 뒤 다시 읽는다 — 제품을 골랐으면 컨디션 후보도(기간마다 다르다) */
  private reloadDayPeriod(): void {
    const dayQuery = this.data.dayQuery;
    const product = this.data.dayPick.product;
    if (product === null) { void this.loadDays(); return; }
    void this.pickDayProduct({ id: dayQuery.product_id, ...product }, dayQuery.condition);
  }

  handleDayFrom(_e: Event, el: HTMLElement): void { this.setDayPeriod('from', (el as HTMLInputElement).value); }
  handleDayTo(_e: Event, el: HTMLElement): void { this.setDayPeriod('to', (el as HTMLInputElement).value); }

  handleDayCondition(_e: Event, el: HTMLElement): void {
    this.data = { ...this.data, dayQuery: { ...this.data.dayQuery, condition: (el as HTMLSelectElement).value } };
    void this.loadDays();
  }

  handleOpenDayPn(_e: Event, el: HTMLElement): void {
    const id = el.dataset.id ?? '';
    assert(id !== '', '파트넘버 버튼은 data-id(hclt_product_id)를 가진다 — renderDayToolbar 가 붙였다');
    window.open(`/products/${encodeURIComponent(id)}`, '_blank', 'noopener');
  }

  // ─── 이력 ───
  /** 지금 eventQuery 로 한 묶음. 실패면 error 글. 끝나면 본문 다시 그림 */
  private async loadEvents(): Promise<void> {
    const seq = ++this.loadSeq;
    this.data = { ...this.data, loading: true, error: '' };
    this.refreshBody(() => this.renderEvents());
    try {
      const events = await api.fetchUnitEvents(this.data.eventQuery);
      if (seq !== this.loadSeq) return;
      this.data = { ...this.data, events, loading: false };
    } catch (e) {
      if (seq !== this.loadSeq) return;
      this.data = { ...this.data, loading: false, error: e instanceof Error ? e.message : String(e) };
    }
    this.refreshBody(() => this.renderEvents());
  }

  private renderEvents(): string {
    const { eventQuery, events, loading, error } = this.data;
    const rows = events === null ? [] : events.rows;
    const emptyWord = loading ? '불러오는 중…' : error !== '' ? `불러오지 못했습니다 — ${escapeHtml(error)}` : '맞는 줄이 없습니다';
    const body = rows.length === 0 ? `<tr><td colspan="9" class="text-muted">${emptyWord}</td></tr>` : rows.map((event, index) => this.renderEventRow(event, eventQuery.offset + index)).join('');

    return `${this.renderEventToolbar(eventQuery, events)}
      <table class="pv-table pv-events">
        <thead><tr>${ROW_NO_HEAD}<th>시각</th><th>무슨 일</th><th>내용</th><th>파트넘버</th><th>시리얼</th><th>누가</th><th>사유</th><th>실물</th></tr></thead>
        <tbody>${body}</tbody>
      </table>`;
  }

  /** 변동표 도구 줄 — 기간 둘 · 찾기 · 종류 · 사유 · 페이징. 제품 · 업로드로 걸렀으면 칩 */
  private renderEventToolbar(eventQuery: UnitEventListQuery, events: UnitEventLineViewList | null): string {
    const option = (value: string, label: string, current: string): string => `<option value="${escapeHtml(value)}" ${current === value ? 'selected' : ''}>${escapeHtml(label)}</option>`;
    return `
      <div class="pc-filter-bar">
        <label class="pc-date"><input type="date" data-action="event-from" value="${escapeHtml(eventQuery.from)}"> ~ <input type="date" data-action="event-to" value="${escapeHtml(eventQuery.to)}"></label>
        ${this.renderPeriodPresets('events', eventQuery.from, eventQuery.to)}
        <label class="pc-search">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
          <input type="search" data-action="event-query" placeholder="파트넘버 · 시리얼 · 실물 번호" value="${escapeHtml(eventQuery.q)}">
        </label>
        <select class="pc-select" data-action="event-type" aria-label="무슨 일">
          ${option('', '전체 일', eventQuery.types.join(','))}${api.E_INVENTORY_EVENT_TYPES.map((type) => option(type, EVENT_WORD[type], eventQuery.types.join(','))).join('')}
        </select>
        <select class="pc-select" data-action="event-reason" aria-label="사유">
          ${option('', '전체 사유', eventQuery.reason)}${api.E_INVENTORY_EVENT_REASONS.map((reason) => option(reason, REASON_WORD[reason], eventQuery.reason)).join('')}
        </select>
        ${eventQuery.product_id === '' ? '' : `<span class="pv-product-chip">제품 #${escapeHtml(eventQuery.product_id)} 의 일만 <button type="button" data-action="event-clear-product">전체 보기</button></span>`}
        ${eventQuery.stocktake_id === '' ? '' : `<span class="pv-product-chip">업로드 #${escapeHtml(eventQuery.stocktake_id)} 이 바꾼 것만 <button type="button" data-action="event-clear-stocktake">전체 보기</button></span>`}
        <span class="pv-spacer"></span>
        ${events === null ? '' : renderPagerLine(events, 'event-page')}
      </div>`;
  }

  /** 이벤트 한 줄 — 파트넘버는 재고 상세 새 탭, 실물 번호는 실물 상세 새 탭 */
  private renderEventRow(event: UnitEventLineView, no: number): string {
    const words = eventWordsOf(event);
    const reason = event.reason === null ? '' : `${REASON_WORD[event.reason]}${event.reason_note === null ? '' : ` · ${escapeHtml(event.reason_note)}`}`;
    return `
      <tr>
        ${rowNoCell(no, 0)}
        <td class="pl-sub mono">${escapeHtml(fmtDateTime(event.created_at))}</td>
        <td class="pl-main"><span class="chip pv-ev-${event.type}">${escapeHtml(words.chip)}</span></td>
        <td class="pl-sub pl-desc"><div class="pl-clamp">${escapeHtml(words.detail)}</div></td>
        <td class="pl-main"><button type="button" class="pv-pn mono" data-action="event-pn" data-id="${event.hclt_product_id}"><b>${escapeHtml(pnTextOf(event))}</b></button> <small class="text-muted">${escapeHtml(event.condition)}</small></td>
        <td class="pl-sub mono">${event.serial_number === null ? '—' : escapeHtml(event.serial_number)}</td>
        <td class="pl-sub">${event.changed_by === null ? '—' : escapeHtml(event.changed_by)}</td>
        <td class="pl-sub">${reason}</td>
        <td class="pl-sub pl-id mono"><a data-link href="/inventory/units/${event.unit_id}" target="_blank" rel="noopener" title="실물 상세">#${event.unit_id}</a></td>
      </tr>`;
  }

  /** 기간 프리셋 버튼 줄 — 지금 기간과 같은 버튼에 켜진 표시. 이력의 끝 '' 은 "오늘까지" 로 본다 */
  private renderPeriodPresets(tab: PeriodTab, from: string, to: string): string {
    const today = kstDateOf(0);
    const bToToday = to === today || to === '';
    const buttons = PERIOD_PRESETS.map((preset) => {
      const bOn = bToToday && from === kstDateOf(preset.days);
      return `<button type="button" class="pc-preset${bOn ? ' is-on' : ''}" data-action="period-preset" data-tab="${tab}" data-days="${preset.days}">${escapeHtml(preset.label)}</button>`;
    }).join('');
    return `<span class="pc-presets">${buttons}</span>`;
  }

  /** 기간 프리셋 — 그 탭의 기간을 [오늘 − days, 오늘] 로 한 번에 바꾸고 다시 읽는다 */
  handlePeriodPreset(_e: Event, el: HTMLElement): void {
    const tab = el.dataset.tab as PeriodTab;
    const days = Number(el.dataset.days);
    assert(PERIOD_TABS.includes(tab) && Number.isInteger(days) && days >= 0, '프리셋 버튼은 data-tab(lines · flows · days · events)과 data-days(0 이상 정수)를 가진다 — renderPeriodPresets 가 붙였다');
    const from = kstDateOf(days);
    const to = kstDateOf(0);
    switch (tab) {
      case 'lines':
        this.setLineQuery({ from, to });
        break;
      case 'flows':
        this.data = { ...this.data, flowQuery: { ...this.data.flowQuery, from, to, offset: 0 } };
        void this.loadFlows();
        break;
      case 'days':
        this.data = { ...this.data, dayQuery: { ...this.data.dayQuery, from, to } };
        this.reloadDayPeriod();
        break;
      case 'events':
        this.setEventQuery({ from, to });
        break;
      default:
        throw new Error(`unhandled switch case: ${tab satisfies never}`);
    }
  }

  private setEventQuery(patch: Partial<UnitEventListQuery>): void {
    this.data = { ...this.data, eventQuery: { ...this.data.eventQuery, ...patch, offset: 0 } };
    void this.loadEvents();
  }

  handleEventQuery(_e: Event, el: HTMLElement): void {
    this.data = { ...this.data, eventQuery: { ...this.data.eventQuery, q: (el as HTMLInputElement).value, offset: 0 } };
    if (this.queryTimer !== null) clearTimeout(this.queryTimer);
    this.queryTimer = setTimeout(() => { this.queryTimer = null; void this.loadEvents(); }, QUERY_DEBOUNCE_MS);
  }

  handleEventFrom(_e: Event, el: HTMLElement): void { this.setEventQuery({ from: (el as HTMLInputElement).value }); }
  handleEventTo(_e: Event, el: HTMLElement): void { this.setEventQuery({ to: (el as HTMLInputElement).value }); }
  handleEventClearProduct(): void { this.setEventQuery({ product_id: '' }); }
  handleEventClearStocktake(): void { this.setEventQuery({ stocktake_id: '' }); }

  handleEventType(_e: Event, el: HTMLElement): void {
    const value = (el as HTMLSelectElement).value;
    const type = api.E_INVENTORY_EVENT_TYPES.find((t) => t === value);
    this.setEventQuery({ types: type === undefined ? [] : [type] });   // 이력 탭은 하나만 고른다
  }

  handleEventReason(_e: Event, el: HTMLElement): void {
    const value = (el as HTMLSelectElement).value;
    const reason = api.E_INVENTORY_EVENT_REASONS.find((r) => r === value);
    this.setEventQuery({ reason: reason === undefined ? '' : reason });
  }

  handleEventPage(_e: Event, el: HTMLElement): void {
    const offset = Number(el.dataset.offset);
    assert(Number.isInteger(offset) && offset >= 0, '페이저 버튼은 data-offset(그 쪽의 offset)을 가진다 — renderPagerLine 이 붙였다');
    this.data = { ...this.data, eventQuery: { ...this.data.eventQuery, offset } };
    void this.loadEvents();
  }

  handleOpenEventPn(_e: Event, el: HTMLElement): void {
    const id = el.dataset.id ?? '';
    assert(id !== '', '파트넘버 버튼은 data-id(hclt_product_id)를 가진다 — renderEventRow 가 붙였다');
    window.open(stockDetailHrefOf(id, '', ''), '_blank', 'noopener');   // 재고 상세(2026-10-26 — 회의 §15)
  }

  // ─── 핸들러 — 구매 건 ───
  /** 줄 클릭 → 재고 상세 새 탭. 긁어 복사(드래그)면 안 연다(회의 2026-10-21 · 2026-10-26 — 실물 탭으로 걸러 가던 길은 뺌, 재고 상세에 실물이 있다) */
  handleOpenLine(_e: Event, el: HTMLElement): void {
    if (hasTextSelection()) return;
    const id = el.dataset.id ?? '';
    assert(id !== '', '구매 건 줄은 data-id(hclt_product_id)를 가진다 — renderStockLineRow 가 붙였다');
    window.open(stockDetailHrefOf(id, el.dataset.condition ?? '', el.dataset.lineKey ?? ''), '_blank', 'noopener');   // 누른 줄의 컨디션 · 로트를 먼저 고른 채(회의 2026-10-28 §0 6 · 로트 2026-11-04)
  }

  handleLineFrom(_e: Event, el: HTMLElement): void { this.setLineQuery({ from: (el as HTMLInputElement).value }); }
  handleLineTo(_e: Event, el: HTMLElement): void { this.setLineQuery({ to: (el as HTMLInputElement).value }); }
  handleLineCategory(_e: Event, el: HTMLElement): void { this.setLineQuery({ category: (el as HTMLSelectElement).value }); }
  handleLinePeriodAll(): void { this.setLineQuery({ from: '', to: '' }); }

  /** [구매일] — 날짜 줄을 펼치고 접는다. 접어도 걸린 기간은 그대로(버튼 글이 보여 준다) */
  handleLinePeriodToggle(): void {
    this.data = { ...this.data, bLinePeriodOpen: !this.data.bLinePeriodOpen };
    this.refreshBody(() => this.renderLines());
  }

  /** 목록 질의 칸 몇 개를 바꾸고 첫 쪽부터 다시 읽는다. 시작이 끝보다 늦으면 서버가 400 — 화면은 끝을 시작에 맞춘다 */
  private setLineQuery(patch: Partial<StockLineListQuery>): void {
    const next: StockLineListQuery = { ...this.data.lineQuery, ...patch, offset: 0 };
    const bReversed = next.from !== '' && next.to !== '' && next.from > next.to;
    this.data = { ...this.data, lineQuery: bReversed ? { ...next, to: next.from } : next };
    void this.loadLines();
  }

  handleLineQuery(_e: Event, el: HTMLElement): void {
    this.data = { ...this.data, lineQuery: { ...this.data.lineQuery, q: (el as HTMLInputElement).value, offset: 0 } };
    if (this.queryTimer !== null) clearTimeout(this.queryTimer);
    this.queryTimer = setTimeout(() => { this.queryTimer = null; void this.loadLines(); }, QUERY_DEBOUNCE_MS);
  }

  /** 쪽 이동 — 버튼의 data-offset 으로 */
  handleLinePage(_e: Event, el: HTMLElement): void {
    const offset = Number(el.dataset.offset);
    assert(Number.isInteger(offset) && offset >= 0, '페이저 버튼은 data-offset(그 쪽의 offset)을 가진다 — renderPagerLine 이 붙였다');
    this.data = { ...this.data, lineQuery: { ...this.data.lineQuery, offset } };
    void this.loadLines();
  }

  // ─── 핸들러 — 현황 ───
  /** 줄 → 실물 상세 새 탭(2026-11-03 — "실물 표는 누르면 실물 상세만". 옛 파트넘버 → 재고 상세는 뺐다). 체크칸은 '@check' 가 먼저 받는다 */
  handleOpenUnit(_e: Event, el: HTMLElement): void {
    if (hasTextSelection()) return;   // 긁어 복사(드래그)면 안 연다
    const id = el.dataset.id ?? '';
    assert(id !== '', '실물 줄은 data-id(실물 id)를 가진다 — renderUnitTable 의 work 줄이 붙였다');
    window.open(`/inventory/units/${encodeURIComponent(id)}`, '_blank', 'noopener');
  }

  handleCheck(_e: Event, el: HTMLElement): void {
    const id = el.dataset.id ?? '';
    const next = new Set(this.data.checked);
    if (next.has(id)) next.delete(id); else next.add(id);
    this.data = { ...this.data, checked: next };
    this.refreshBody(() => this.renderUnits());
  }

  /** 필터 줄 오른쪽 — 평소엔 [작업] 하나(누르면 고르기 모드), 고르기 모드면 "N장 선택 · [작업 ▾] · [끝내기]" */
  private renderPickBar(rows: readonly UnitView[]): string {
    if (!this.data.bPicking) return `<button type="button" class="btn-ghost" data-action="pick-start" title="실물을 골라 홀딩 · 사용 불가 등을 합니다">작업</button>`;
    const checkedCount = this.data.checked.size;
    return `
      <span class="pv-actions ${checkedCount === 0 ? 'disabled' : ''}">
        <b>${checkedCount === 0 ? '줄을 눌러 고르세요' : `${checkedCount}장 선택`}</b>
        <span class="pv-menu-wrap">
          <button type="button" class="btn-ghost" data-action="menu-toggle" ${checkedCount === 0 || this.data.acting ? 'disabled' : ''}>작업 ▾</button>
          ${this.renderActionMenu(rows)}
        </span>
        <button type="button" class="btn-ghost" data-action="pick-end">끝내기</button>
      </span>`;
  }

  /** [작업 ▾] 메뉴 — 줄마다 이름 · 설명 한 줄. 고른 실물 전부에 되는 작업만 켜지고, 안 되는 줄은 회색 + 까닭(아직 없는 작업은 "준비 중"). 닫혀 있으면 '' */
  private renderActionMenu(rows: readonly UnitView[]): string {
    if (!this.data.menuOpen) return '';
    const picked = rows.filter((unit) => this.data.checked.has(unit.id));
    const items = E_UNIT_ACTIONS.map((action) => {
      const blockOrNull = unitActionBlockOf(action, picked);
      return `
        <button type="button" class="pv-menu-item" data-action="menu-item" data-kind="${action}" ${blockOrNull === null ? '' : 'disabled'}>
          <span class="pv-menu-word">${UNIT_ACTION_WORD[action]}${blockOrNull === null ? '' : ` <small class="pv-menu-block">${escapeHtml(blockOrNull)}</small>`}</span>
          <small class="pv-menu-note">${UNIT_ACTION_NOTE[action]}</small>
        </button>`;
    });
    return `<div class="pl-notes-overlay" data-action="menu-toggle"></div><div class="pv-menu">${items.join('')}</div>`;
  }

  /** [작업] — 고르기 모드로. 체크 칸이 나오고 줄을 누르면 체크된다 */
  handlePickStart(): void {
    this.data = { ...this.data, bPicking: true, checked: new Set(), actionError: '' };
    this.refreshBody(() => this.renderUnits());
  }

  /** [끝내기] — 고르기 모드를 닫는다. 고른 것 · 열린 메뉴도 비운다 */
  handlePickEnd(): void {
    this.data = { ...this.data, bPicking: false, checked: new Set(), menuOpen: false };
    this.refreshBody(() => this.renderUnits());
  }

  handleMenuToggle(): void {
    this.data = { ...this.data, menuOpen: !this.data.menuOpen };
    this.refreshBody(() => this.renderUnits());
  }

  /** 메뉴 한 줄 — 홀딩 · 자산으로는 메모 창, 사용 불가는 사유 창, 풀기 · 자산 회수 · 다시 찾음은 바로. 검수 완료 · 이동은 잠겨 있어 안 온다 */
  handleMenuItem(_e: Event, el: HTMLElement): void {
    const kind = E_UNIT_ACTIONS.find((action) => action === el.dataset.kind);
    assert(kind !== undefined, `메뉴 줄의 data-kind 는 E_UNIT_ACTIONS 중 하나 — renderActionMenu 가 붙였다: ${el.dataset.kind ?? ''}`);
    this.data = { ...this.data, menuOpen: false };
    switch (kind) {
      case 'hold':
      case 'asset':        this.handleNoteOpen(kind); return;
      case 'release':      void this.handleRelease(); return;
      case 'asset_return': void this.actOnChecked((id) => api.returnUnitFromAsset(id), UNIT_ACTION_WORD.asset_return); return;
      case 'retire':       this.openRetire([...this.data.checked]); return;
      case 'restore':      void this.handleRestore(); return;
      case 'inspect':
      case 'move':         throw new Error(`"${UNIT_ACTION_WORD[kind]}" 은 아직 없는 작업 — renderActionMenu 가 메뉴 줄을 disabled 로 잠갔다(unitActionBlockOf 의 NOT_BUILT_WORD)`);
      default:             throw new Error(`unhandled switch case: ${kind satisfies never}`);
    }
  }

  /** 홀딩 창 — "홀딩 N장" · 메모 칸 · [실행] · [취소]. 메모가 비면 [실행] 잠김. 창이 닫혀 있으면 '' */
  /** 메모 창 — 홀딩 · 자산으로 둘이 같은 창(글만 다르다, NOTE_DIALOG_TEXT). actionNote 가 null 이면 닫힘 */
  private renderNoteDialog(): string {
    const note = this.data.actionNote;
    if (note === null) return '';
    const action = this.data.noteAction;
    const text = NOTE_DIALOG_TEXT[action];
    return `
      <div class="mg-wrap" role="dialog" aria-label="${UNIT_ACTION_WORD[action]}">
        <div class="mg-overlay" data-action="note-cancel"></div>
        <div class="mg-dialog pv-hold-dialog">
          <h3 class="mg-title">${UNIT_ACTION_WORD[action]} ${this.data.checked.size}장</h3>
          <p class="mg-note">${text.note}</p>
          <input type="text" class="pv-hold-note" data-action="action-note" placeholder="${text.placeholder}" value="${escapeHtml(note)}" autofocus>
          <div class="mg-buttons">
            <button type="button" class="btn-ghost" data-action="note-cancel">취소</button>
            <button type="button" class="btn-primary" data-action="note-confirm" ${note.trim() === '' || this.data.acting ? 'disabled' : ''}>${this.data.acting ? '보내는 중…' : '실행'}</button>
          </div>
        </div>
      </div>`;
  }

  private handleNoteOpen(action: ENoteAction): void {
    this.data = { ...this.data, actionNote: '', noteAction: action };
    this.refreshBody(() => this.renderUnits());
    this._rootElement?.querySelector<HTMLInputElement>('.pv-hold-note')?.focus();
  }

  handleNoteCancel(): void {
    if (this.data.acting) return;
    this.data = { ...this.data, actionNote: null };
    this.refreshBody(() => this.renderUnits());
  }

  /** 메모 글자마다 — 본문을 다시 그리면 칸의 포커스가 날아가니 [실행] 버튼의 잠김만 손으로 바꾼다 */
  handleActionNote(_e: Event, el: HTMLElement): void {
    this.data = { ...this.data, actionNote: (el as HTMLInputElement).value };
    const button = this._rootElement?.querySelector<HTMLButtonElement>('button[data-action="note-confirm"]');
    if (button) button.disabled = this.data.actionNote === null || this.data.actionNote.trim() === '' || this.data.acting;
  }

  /** 창의 [실행] — 고른 실물 전부에 그 작업, 한 장씩 보낸다(서버 라우트가 한 장). 안 되는 장은 모아 보이고 나머지는 된다 */
  async handleNoteConfirm(): Promise<void> {
    const raw = this.data.actionNote;
    assert(raw !== null && raw.trim() !== '', '[실행] 은 메모 창이 열려 있고 메모가 있을 때만 켜진다 — renderNoteDialog 가 disabled 를 붙였다');
    const note = raw.trim();
    const action = this.data.noteAction;
    switch (action) {
      case 'hold':  await this.actOnChecked((id) => api.holdUnit(id, note), UNIT_ACTION_WORD.hold); return;
      case 'asset': await this.actOnChecked((id) => api.moveUnitToAsset(id, note), UNIT_ACTION_WORD.asset); return;
      default: throw new Error(`unhandled switch case: ${action satisfies never}`);
    }
  }

  async handleRelease(): Promise<void> {
    await this.actOnChecked((id) => api.releaseUnit(id), '풀기');
  }

  private async actOnChecked(act: (id: string) => Promise<unknown>, word: string): Promise<void> {
    const ids = [...this.data.checked];
    assert(ids.length > 0, `[${word}] 는 고른 실물이 있을 때만 켜진다 — renderUnits 가 disabled 를 붙였다`);
    this.data = { ...this.data, acting: true };
    const failed: string[] = [];
    for (const id of ids) {
      try { await act(id); } catch (e) { failed.push(`#${id} — ${e instanceof Error ? e.message : String(e)}`); }
    }
    this.data = { ...this.data, acting: false, checked: new Set(), actionNote: null, actionError: failed.length === 0 ? '' : `${word} 안 된 실물 ${failed.length}장 — ${failed.join(' · ')}` };
    await this.loadUnits();
  }

  // ─── 재고 조정 — 사용 불가 · 다시 찾음 · 시리얼로 찾기(2026-11-03) ───
  private openRetire(unitIds: string[]): void {
    assert(unitIds.length > 0, '[사용 불가] 는 옮길 실물이 있을 때만 열린다 — 메뉴 · 시리얼 창이 0장이면 잠근다');
    this.data = { ...this.data, retire: { unit_ids: unitIds, reason: 'lost', note: '' } };
    this.refreshBody(() => this.renderUnits());
  }

  handleRetireCancel(): void {
    if (this.data.acting) return;
    this.data = { ...this.data, retire: null };
    this.refreshBody(() => this.renderUnits());
  }

  handleRetireReason(_e: Event, el: HTMLElement): void {
    const reason = api.E_RETIRED_REASONS.find((value) => value === (el as HTMLInputElement).value);
    assert(reason !== undefined && this.data.retire !== null, '사유 라디오는 [사용 불가] 창 안에만 있고 값은 E_RETIRED_REASONS 중 하나 — renderRetireDialog 가 그렸다');
    this.data = { ...this.data, retire: { ...this.data.retire, reason } };
    this.refreshBody(() => this.renderUnits());
  }

  /** 글자마다 — 다시 그리면 포커스가 날아가니 [사용 불가로] 버튼의 잠김만 손으로 */
  handleRetireNote(_e: Event, el: HTMLElement): void {
    const draft = this.data.retire;
    assert(draft !== null, '글 칸은 [사용 불가] 창 안에만 있다');
    const next = { ...draft, note: (el as HTMLInputElement).value };
    this.data = { ...this.data, retire: next };
    const button = this._rootElement?.querySelector<HTMLButtonElement>('button[data-action="retire-confirm"]');
    if (button) button.disabled = !isRetireReady(next) || this.data.acting;
  }

  /** [사용 불가로] — 한 번에 보낸다(서버가 한 트랜잭션 — 한 장이라도 막히면 전부 안 옮긴다) */
  async handleRetireConfirm(): Promise<void> {
    const draft = this.data.retire;
    assert(draft !== null && isRetireReady(draft), '[사용 불가로] 는 창이 열려 있고 기타면 글이 있을 때만 켜진다 — renderRetireDialog 가 disabled 를 붙였다');
    this.data = { ...this.data, acting: true };
    this.refreshBody(() => this.renderUnits());
    let actionError = '';
    try {
      await api.retireUnits(retireInputOf(draft));
    } catch (e) {
      actionError = `사용 불가 안 됨(한 장도 안 옮김) — ${e instanceof Error ? e.message : String(e)}`;
    }
    this.data = { ...this.data, acting: false, retire: null, checked: new Set(), actionError };
    await this.loadUnits();
  }

  async handleRestore(): Promise<void> {
    if (!window.confirm(`고른 ${this.data.checked.size}장을 다시 찾음 — 사용 불가에서 검수 중으로 돌립니다.`)) return;
    await this.actOnChecked((id) => api.restoreUnit(id), UNIT_ACTION_WORD.restore);
  }

  /** 상태 하나 — 출고 · 이탈을 고르면 서버의 show_gone 을 같이 켠다(기본 목록은 보유만) */
  handleStatusFilter(_e: Event, el: HTMLElement): void {
    const status = (el as HTMLSelectElement).value as EStockStatus | '';
    const bGone = status !== '' && api.GONE_STATUSES.includes(status);
    this.data = { ...this.data, query: { ...this.data.query, status, show_gone: bGone, offset: 0 } };
    void this.loadUnits();
  }

  /** 제품 필터를 푼다 — 전체 실물로 */
  handleClearProduct(): void {
    this.data = { ...this.data, query: { ...this.data.query, product_id: '', offset: 0 } };
    void this.loadUnits();
  }

  handleQuery(_e: Event, el: HTMLElement): void {
    this.data = { ...this.data, query: { ...this.data.query, q: (el as HTMLInputElement).value, offset: 0 } };
    if (this.queryTimer !== null) clearTimeout(this.queryTimer);
    this.queryTimer = setTimeout(() => { this.queryTimer = null; void this.loadUnits(); }, QUERY_DEBOUNCE_MS);
  }

  /** 쪽 이동 — 버튼의 data-offset 으로 */
  handlePage(_e: Event, el: HTMLElement): void {
    const offset = Number(el.dataset.offset);
    assert(Number.isInteger(offset) && offset >= 0, '페이저 버튼은 data-offset(그 쪽의 offset)을 가진다 — renderPagerLine 이 붙였다');
    this.data = { ...this.data, query: { ...this.data.query, offset } };
    void this.loadUnits();
  }

}
